import { createContext, useContext } from 'react';
import type { AccountProfile } from '@mypoz/shared';

export interface AccountState {
  /**
   * The profile, or undefined until the vendor has answered — and after it fails.
   *
   * There is no stand-in. An earlier version showed a fixture customer here so the header and the
   * warehouse address always had an ID to print; that put a customer number nobody owned on a
   * shipping address. Now nothing is shown that was not read from the record.
   */
  readonly profile: AccountProfile | undefined;
  readonly status: 'loading' | 'ready' | 'error';
  /** Asks the vendor again. Safe to call at any time; a fetch already in flight is discarded. */
  reload: () => void;
  /**
   * Reads the record again *without* clearing it first (MYP-92). `reload` empties the profile while
   * it loads, and `SessionGate` then unmounts the whole page — a pay dialog included. This keeps the
   * old record on screen until the new one arrives, and keeps it if the read fails.
   *
   * Resolves `true` once a new record is in, `false` when the read failed (the old one is kept) —
   * so a caller can tell "the answer is still no" from "there was no answer" (MYP-142 review).
   */
  refresh: () => Promise<boolean>;
}

export const AccountContext = createContext<AccountState | null>(null);

/**
 * The session as it stands, loaded or not.
 *
 * For the few things that sit *outside* the gate — the header, and the wallet that seeds itself
 * from the record — which must render something while the account is still loading. Everything
 * inside the page area uses `useAccountProfile` instead.
 */
export function useAccountSession(): AccountState {
  const value = useContext(AccountContext);
  if (!value) throw new Error('useAccountSession must be used inside AccountProvider');
  return value;
}

/**
 * The loaded profile, for screens that only ever render once the account is known.
 *
 * `SessionGate` wraps the page area and mounts its children only when the profile has arrived, so
 * a screen calling this never has to handle "no account yet" — and cannot, by mistake, show a
 * blank or invented customer ID. Calling it from outside the gate is a bug, and throws.
 */
export function useAccountProfile(): { profile: AccountProfile; reload: () => void } {
  const { profile, reload } = useAccountSession();
  if (!profile) {
    throw new Error(
      'useAccountProfile was called before the account loaded — is it inside SessionGate?',
    );
  }
  return { profile, reload };
}
